import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router'
import Layout from './Layout'
import { DataRoomsContext, StatusContext } from './Contexts'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Typography,
  TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CheckIcon from '@mui/icons-material/Check'
import StartTaskButton from './StartTaskButton'
import GridContainer from './GridContainer'
import AButton from './AButton'
import AddDataRoomDialog from './AddDataRoomDialog'
import GridCard from './GridCard'
import { LoadingDotsIcon } from './Icons'
import CardHeader from './CardHeader'
import { QAListBranches } from 'modules/Rest'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/AddCircleOutline'

const useStyles = makeStyles(() => ({
  tableRow: {
    '& th:not(:first-child)': { textAlign: 'center' },
    '& td:not(:first-child)': { textAlign: 'center' }
  },
  dataRoomsTable: {
    '& .MuiTableCell-root': {
      paddingLeft: 4,
      paddingRight: 4,
    },
    '& .MuiButton-root': {
      minWidth: 0,
      paddingLeft: 6,
      paddingRight: 6,
      whiteSpace: 'normal',
      textAlign: 'center',
    },
  },
  loadingDots: {
    paddingBottom: 10,
    paddingLeft: 10,
    display: 'inline-block',
  },
  modalRow: {
    display: 'flex',
    '& > *': { flex: 1 },
    '& > *:last-child': { flex: 2 },
  },
  modalLabel: {
    marginTop: 3,
  },
  minimumWidth: {
    width: 250,
  },
}))


function DataRooms() {
  const classes = useStyles()
  const navigate = useNavigate()
  const { dataRooms, loading } = useContext(DataRoomsContext)
  const { sessionStatus } = useContext(StatusContext)
  const [currentDataRoom, setCurrentDataRoom] = useState(false)
  const [showAddRoomDialog, setShowAddRoomDialog] = useState('')
  const [showCopyRoomDialog, setShowCopyRoomDialog] = useState('')
  const [copySourceRoom, setCopySourceRoom] = useState(null)
  const [showQAOptions, setShowQAOptions] = useState(false)
  const [qBranches, setQBranches] = useState([])
  const [qBranchesLoading, setQBranchesLoading] = useState(false)
  const [selectedQBranch, setSelectedQBranch] = useState('')
  const [incl_unaccepted, setIncl_unaccepted] = useState(false)
  const [incl_unpublished_a, setIncl_unpublished_a] = useState(false)
  const [incl_attention_from, setIncl_attention_from] = useState(false)
  const [incl_creators, setIncl_creators] = useState(false)
  const [incl_detailed_activity, setIncl_detailed_activity] = useState(false)
  const [incl_unpublished_only, setIncl_unpublished_only] = useState(false)
  const [showDownloadOptions, setShowDownloadOptions] = useState(false)
  const [showIndexOptions, setShowIndexOptions] = useState(false)
  const [selectedIndexKind, setSelectedIndexKind] = useState('DRFolder,DRFile')
  const [include_index, setIncludeIndex] = useState(true)
  const [split_long_paths, setSplitLongPaths] = useState(false)
  const [download_original, setDownloadOriginal] = useState(false)

  return (
    <Layout showRoomSelect={false}>
      <GridContainer>
        <GridCard sm={12} uniformPadding={true}>
          <CardHeader
            title={<>
              <>Data Rooms</>
              {loading ? <div className={classes.loadingDots}><LoadingDotsIcon size={6} vPadding={6} hPadding={6}/></div> : null}
            </>}
            headingElement='h5'
            buttons={[<AButton startIcon={<AddIcon/>} onClick={() => setShowAddRoomDialog('editing')} disabled={!(sessionStatus.allow_light || sessionStatus.allow_full)}>Create data room</AButton>]}
          />
          {!loading || (dataRooms && dataRooms.length) ? <TableContainer>
            <Table size='small' className={classes.dataRoomsTable}>
              <TableHead>
                <TableRow className={classes.tableRow}>
                  <TableCell>Name</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell>Index</TableCell>
                  <TableCell>Contents</TableCell>
                  <TableCell>Contents</TableCell>
                  <TableCell>Q&A</TableCell>
                  <TableCell>Statistics</TableCell>
                  <TableCell>Copy</TableCell>
                  <TableCell>Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataRooms.map((room) => (
                  <TableRow key={room.data_room} className={classes.tableRow}>
                    <TableCell>{room.data_room_display_name}{room.light && <span style={{ fontStyle: 'italic' }}> Light</span>}</TableCell>
                    <TableCell>
                      <span>{!room.disabled && <CheckIcon />}</span>
                    </TableCell>
                    <TableCell>
                      {!room.light &&
                        <AButton
                          onClick={() => {
                            setCurrentDataRoom(room)
                            setShowIndexOptions(true)
                          }}
                        >
                          Download index
                        </AButton>
                      }
                    </TableCell>
                    <TableCell>
                      <StartTaskButton
                        args={room}
                        url={`${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/data_rooms/analyze?use_async=true`}
                        stage_label='Requesting analysis'
                        buttonText='Analyze contents'
                      />
                    </TableCell>
                    <TableCell>
                      <AButton
                        onClick={() => {
                          setShowDownloadOptions(true)
                          setCurrentDataRoom(room)
                          setDownloadOriginal(false)
                        }}
                      >
                        Download as zip
                      </AButton>
                    </TableCell>
                    <TableCell>
                      {room.qa && <AButton
                        onClick={async () => {
                          setShowQAOptions(true)
                          setCurrentDataRoom(room)
                          setSelectedQBranch('_all')
                          setQBranches([])
                          setQBranchesLoading(true)
                          const { data: { success, q_branches } } = await QAListBranches({ data_room: room.data_room })
                          if (success) {
                            setQBranches(q_branches)
                          } else {
                            setQBranches([])
                          }
                          setQBranchesLoading(false)
                        }}
                      >
                        Export Q&A
                      </AButton>}
                    </TableCell>
                    <TableCell>
                      <StartTaskButton
                        args={room}
                        url={`${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/stats/export?use_async=true`}
                        stage_label='Requesting stats'
                        buttonText='Export stats'
                      />
                    </TableCell>
                    <TableCell>
                      <AButton
                        onClick={() => {
                          setCopySourceRoom(room)
                          setShowCopyRoomDialog('editing')
                        }}
                        disabled={!(sessionStatus.allow_light || sessionStatus.allow_full)}
                      >
                        Create copy
                      </AButton>
                    </TableCell>
                    <TableCell>
                      <AButton onClick={() => navigate(`/data-room/${room.data_room}`)} startIcon={<EditIcon/>}>
                        Edit
                      </AButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer> : null}
        </GridCard>
      </GridContainer>

      <AddDataRoomDialog mode={showAddRoomDialog} setMode={setShowAddRoomDialog} />
      <AddDataRoomDialog
        mode={showCopyRoomDialog}
        setMode={setShowCopyRoomDialog}
        sourceDataRoom={copySourceRoom}
        onExited={() => setCopySourceRoom(null)}
      />

      <Dialog open={showDownloadOptions} onClose={() => setShowDownloadOptions(false)}>
        <DialogTitle>Download as Zip</DialogTitle>
        <DialogContent>
          <FormGroup>
            <Tooltip title='Include indexes as part of the file/folder names'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={include_index}
                    onChange={event => setIncludeIndex(event.target.checked)}
                  />
                }
                label='Include indexes in file/folder names'
              />
            </Tooltip>
            <Tooltip title='Split long paths to maximize zip file compatibility'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={split_long_paths}
                    onChange={event => setSplitLongPaths(event.target.checked)}
                  />
                }
                label='Split long paths'
              />
            </Tooltip>
            {currentDataRoom?.download_original ? (
              <Tooltip title='Default is the redacted versions; originals require the download-originals entitlement in the data room'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={download_original}
                      onChange={event => setDownloadOriginal(event.target.checked)}
                    />
                  }
                  label='Originals, without redactions'
                />
              </Tooltip>
            ) : null}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <AButton onClick={() => setShowDownloadOptions(false)}>
            Cancel
          </AButton>
          <StartTaskButton
            args={{...currentDataRoom, include_index, split_long_paths,
              request_original: currentDataRoom?.download_original ? download_original : false}}
            url={`${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/data_rooms/download?use_async=true`}
            stage_label='Requesting download'
            buttonText='Download as zip'
            whenDone={() => setShowDownloadOptions(false)}
            buttonTimeout={false}
          />
        </DialogActions>
      </Dialog>

      <Dialog open={showIndexOptions} onClose={() => setShowIndexOptions(false)}>
        <DialogTitle>Download index</DialogTitle>
        <DialogContent>
          <FormGroup>
            <div className={classes.minimumWidth}/>
            <div className={classes.modalRow}>
              <Typography variant='body2' className={classes.modalLabel}>Include:</Typography>
              <TextField
                select
                variant="standard"
                value={selectedIndexKind}
                onChange={event => setSelectedIndexKind(event.target.value)}
              >
                <MenuItem value='DRFolder,DRFile'>Files and folders</MenuItem>
                <MenuItem value='DRFolder'>Folders only</MenuItem>
              </TextField>
            </div>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <AButton onClick={() => {
            setShowIndexOptions(false)
            setSelectedIndexKind("DRFolder,DRFile")
          }}>
            Cancel
          </AButton>

          <StartTaskButton
            args={{ ...currentDataRoom, kind: selectedIndexKind }}
            url={`${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/data_rooms/create_index?use_async=true`}
            stage_label="Requesting index"
            buttonText="Download index"
            whenDone={() => {
              setShowIndexOptions(false)
              setSelectedIndexKind("DRFolder,DRFile")
            }}
            buttonTimeout={false}
          />
        </DialogActions>
      </Dialog>

      <Dialog open={showQAOptions} onClose={() => setShowQAOptions(false)}>
        <DialogTitle>Export Q&A</DialogTitle>
        <DialogContent>
          <FormGroup>
            <Tooltip title='Include questions that are waiting to be accepted'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incl_unaccepted}
                    onChange={event => setIncl_unaccepted(event.target.checked)}
                  />
                }
                label='Include questions not yet accepted'
              />
            </Tooltip>
            <Tooltip title='Includes draft answers and unconfirmed answers'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incl_unpublished_a}
                    onChange={event => setIncl_unpublished_a(event.target.checked)}
                  />
                }
                label='Include unconfirmed answers'
              />
            </Tooltip>
            <Tooltip title='Includes the users and groups currently responsible for each question'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incl_attention_from}
                    onChange={event => setIncl_attention_from(event.target.checked)}
                  />
                }
                label='Include responsible'
              />
            </Tooltip>
            <Tooltip title='Includes the user that initially submitted each question'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incl_creators}
                    onChange={event => setIncl_creators(event.target.checked)}
                  />
                }
                label='Include submitter'
              />
            </Tooltip>
            <Tooltip title='Includes more detailed activity logs (incl. user information)'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incl_detailed_activity}
                    onChange={event => setIncl_detailed_activity(event.target.checked)}
                  />
                }
                label='Include detailed activity'
              />
            </Tooltip>
            <Tooltip title='Only includes the questions for which an answer has not been published'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incl_unpublished_only}
                    onChange={event => setIncl_unpublished_only(event.target.checked)}
                  />
                }
                label='Only include outstanding questions'
              />
            </Tooltip>
            {qBranchesLoading && <LoadingDotsIcon size={6} vPadding={12} hPadding={6}/>}
            {qBranches.length > 1 && <div className={classes.modalRow}>
              <Typography variant='body2' className={classes.modalLabel}>Branch:</Typography>
              <TextField
                select
                variant="standard"
                value={selectedQBranch}
                onChange={event => setSelectedQBranch(event.target.value)}
              >
                <MenuItem value='_all'>All</MenuItem>
                {qBranches.map(({ name, q_branch }) => <MenuItem value={q_branch}>{name}</MenuItem>)}
              </TextField>
            </div>}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <AButton onClick={() => setShowQAOptions(false)}>
            Cancel
          </AButton>
          <StartTaskButton
            args={{...currentDataRoom, incl_unaccepted, incl_unpublished_a, incl_attention_from, incl_creators, incl_detailed_activity, incl_status_int_less_or_equal_than: incl_unpublished_only ? 6 : undefined, q_branch: selectedQBranch}}
            url={`${process.env.REACT_APP_ENDPOINT_BASE_URL}/api/v1/qa/export?use_async=true`}
            stage_label='Requesting Q&A'
            buttonText='Export'
            whenDone={() => setShowQAOptions(false)}
            buttonTimeout={false}
          />
        </DialogActions>
      </Dialog>
    </Layout>
  )
}

export default DataRooms
